import { memo, useCallback, useEffect, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import InfoDot from "@/components/charts/InfoDot";
import type { BrushRange } from "@/stores/useAnalysisStore";
import type { FeatureData, TICData } from "@/types/spectrum";
import { compactIntensity } from "@/utils/compactIntensity";
import { getFeatureColor } from "@/utils/featureMapping";
import { groupFeaturesByObservation } from "@/utils/observationGrouping";
import { panelCls } from "@/utils/panelCls";
import {
  maxIndexInPixelColumn,
  peakIndexNear,
  ticCursorNote,
} from "@/utils/ticCursor";
import { ticNavCaption } from "@/utils/ticNavCaption";
import {
  clusterObservationsForRug,
  nearestRugObservation,
  type RugObservation,
} from "@/utils/ticRug";

/**
 * TIC Plot — uPlot(Canvas) **전량 렌더**.
 *
 * 2026-09-03 (외부 전문가 자문 반영): 종전 recharts(SVG) 구현은 12,270점
 * 크로마토그램에서 사용할 수 없을 만큼 느렸고, 그걸 서버 데시메이션으로
 * 해결하려다 **봉우리를 지우는 결함**을 만들었다. 자문 판정은 명확했다 —
 * 12,270점은 손실 축약할 규모가 아니고, Canvas 로 전량 그리는 쪽이 더 단순
 * 하고 안전하다. 그래서:
 *
 * - **원자료 전량**을 그린다. 서버 축약 없음 → "화면에 없는 피크"가 없다.
 * - 선은 **직선 보간**이다. 종전 recharts `type="monotone"` 은 원자료 점
 *   사이에 없는 매끈한 곡선을 만들어냈다(증거 화면에서는 거짓이다).
 * - 결측(NaN)은 **선을 끊는다**. 앞뒤를 이어버리면 없는 신호를 그리게 된다.
 * - 관찰 표시는 하단 rug 트랙. 화면에서 겹치면 묶어 개수를 적고, 확대하면
 *   저절로 분해된다(임의의 "상위 N" 기준이 필요 없다 — utils/ticRug.ts).
 * - 브러시 선택은 **RT 도메인**으로 내보낸다(배열 인덱스 아님).
 *
 * 접근성: 차트는 보조 수단이다. 관찰 170건을 차트 하나의 role="button" 으로
 * 표현할 수 없고 키보드 이벤트에는 좌표가 없다 — 키보드 선택은 아래 관찰
 * 표(FeaturesTable)가 담당하고, 여기서는 그래픽 역할과 요약 설명만 준다.
 */

interface TICPlotUplotProps {
  ticData: TICData[];
  features: FeatureData[];
  selectedFeature?: string;
  /**
   * 후보 없이 **스캔만** 고른 RT. 측정만 된(회색) MZRT 점을 누르면 물질 선택이
   * 해제되고 이 값만 남는다(운영자 결정 7-a). 종전에는 TIC 가 이 값을 아예
   * 보지 않아, 회색 점을 눌러도 크로마토그램에 아무 표시가 없었다.
   */
  selectedScanRT?: number | null;
  /** 브러시 선택 구간(RT). null 이면 해제. */
  onBrushChange: (range: BrushRange | null) => void;
  onFeatureSelect: (featureId: string) => void;
  /** 관찰에서 먼 빈 영역 클릭 — RT 만 이동. */
  onChartClick?: (rt: number) => void;
}

const CHART_HEIGHT = 300;
/** 하단 개요 막대(네비게이터) 높이 — 종전 recharts Brush 자리를 대신한다. */
const NAV_HEIGHT = 56;
/** rug 트랙 높이(px) — 플롯 하단 안쪽에 그린다. */
const RUG_HEIGHT = 14;
/**
 * 관찰 스냅 반경 — **화면 픽셀** 기준(RT 기준이 아니다).
 *
 * 픽셀 기준이라 확대하면 반경이 저절로 좁아진다: 전체 보기에서 20px 은 RT
 * 2.03분이지만, 10배 확대하면 같은 20px 이 RT 0.20분이다. 즉 "가까이 들여다볼
 * 수록 더 정밀하게 고를 수 있다" — 확대해서 관찰 근처를 자세히 보고 싶을 때
 * 원치 않는 스냅에 걸리지 않는다(운영자 검토 2026-09-03).
 */
const CLICK_TOLERANCE_PX = 20;
const LINE_COLOR = "#8b5cf6";
/**
 * 후보 없이 스캔만 고른 자리의 선 — MZRT 호버가 '후보 없는 스캔'에 쓰는 값과
 * 같다. 두 플롯이 RT 축을 공유하므로 같은 뜻에는 같은 표기를 쓴다.
 */
const SCAN_ONLY_LINE_COLOR = "#607d8b";

/**
 * 지금 보고 있는 구간에서 **화면 1픽셀에 겹치는 원자료 점 수**.
 *
 * 커서 값의 뜻이 이 수에 달려 있다 — 1 이면 커서가 스캔 하나를 가리키고,
 * 13 이면 13개가 겹친 자리에서 그중 최댓값을 가리킨다. 확대·축소마다 달라지므로
 * 화면 문구도 함께 바뀐다(utils/ticCursor.ts 의 ticCursorNote).
 *
 * 확대는 드래그가 끝날 때 1회 일어나므로 여기서 전량을 세도 부담이 없다.
 */
function scansPerPixelIn(
  xs: number[],
  min: number,
  max: number,
  plot: uPlot,
): number {
  const widthCss = plot.bbox.width / devicePixelRatio;
  if (widthCss <= 0) return 1;
  let count = 0;
  for (const x of xs) {
    if (x >= min && x <= max) count += 1;
  }
  return Math.max(count / widthCss, 1);
}

const TICPlotUplot = ({
  ticData,
  features,
  selectedFeature,
  selectedScanRT,
  onBrushChange,
  onFeatureSelect,
  onChartClick,
}: TICPlotUplotProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  /** 커서 값 칩과 그 기준이 되는 감싸개 — 값을 차트 위에 띄운다. */
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const cursorChipRef = useRef<HTMLDivElement>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const navPlotRef = useRef<uPlot | null>(null);
  const fullRangeRef = useRef<{ min: number; max: number } | null>(null);
  const navCleanupRef = useRef<(() => void) | null>(null);
  // 마우스를 올렸을 때 **눌리면 잡힐 관찰** — 선택과 별개의 미리보기.
  // 값이 바뀔 때만 다시 그린다(픽셀마다 redraw 하면 수천 점을 매번 다시
  // 그리게 된다).
  const hoveredObservationIdRef = useRef<string | null>(null);
  // 확대 상태 — 되돌리기 버튼을 **보이게** 하기 위한 것.
  // 종전 recharts Brush 는 막대가 늘 보여 '확대 중'과 '되돌리는 법'이
  // 드러났는데, 드래그 확대만 두니 둘 다 화면에서 사라졌다(운영자 지적:
  // '확대만 되고 원상복구가 안 되네'). 더블클릭으로 되돌아가긴 했지만
  // 아무 데도 그렇게 쓰여 있지 않았다.
  const [isZoomed, setIsZoomed] = useState(false);
  // 지금 보고 있는 구간에서 화면 1픽셀에 겹치는 원자료 점 수.
  // 커서 값의 뜻이 이 수에 따라 달라지므로 화면에 밝힌다(utils/ticCursor.ts).
  const [scansPerPixel, setScansPerPixel] = useState(1);

  // 렌더 중 최신 값을 uPlot 훅에서 읽기 위한 ref (uPlot 인스턴스는 재생성하지
  // 않고 유지한다 — 재생성하면 확대 상태가 매 렌더 초기화된다).
  const observationsRef = useRef<RugObservation[]>([]);
  const selectedRef = useRef<string | undefined>(selectedFeature);
  const selectedScanRef = useRef<number | null>(selectedScanRT ?? null);
  const handlersRef = useRef({ onBrushChange, onFeatureSelect, onChartClick });

  observationsRef.current = groupFeaturesByObservation(features).map(
    (observation) => ({
      id: observation.id,
      rt: observation.rt,
      features: observation.features,
    }),
  );
  selectedRef.current = selectedFeature;
  selectedScanRef.current = selectedScanRT ?? null;
  handlersRef.current = { onBrushChange, onFeatureSelect, onChartClick };

  /** rug 트랙 + 선택 관찰 강조를 캔버스에 그린다. */
  const drawRug = useCallback((u: uPlot) => {
    const ctx = u.ctx;
    const { left, top, width, height } = u.bbox;
    const observations = observationsRef.current;

    const toPixel = (rt: number) => u.valToPos(rt, "x", true) - left;

    // 후보 없이 스캔만 고른 경우 — 그 자리에 **옅은 실선**만 긋는다.
    //
    // rug 눈금은 찍지 않는다: 화면이 "아래 눈금은 AI 후보가 기록된 관찰"이라고
    // 선언했으므로, 후보가 없는 스캔에 눈금을 찍으면 없는 검출을 만드는 것이다.
    // 색은 MZRT 호버가 '후보 없는 스캔'에 쓰는 값과 같다(#607d8b) — 두 플롯이
    // 같은 뜻에 같은 표기를 쓴다.
    if (!selectedRef.current && selectedScanRef.current != null) {
      const x = left + toPixel(selectedScanRef.current);
      if (x >= left && x <= left + width) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(left, top, width, height);
        ctx.clip();
        ctx.strokeStyle = SCAN_ONLY_LINE_COLOR;
        ctx.lineWidth = 1 * devicePixelRatio;
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, top + height);
        ctx.stroke();
        ctx.restore();
      }
    }

    if (observations.length === 0) return;

    const clusters = clusterObservationsForRug(observations, toPixel, width);

    ctx.save();
    ctx.beginPath();
    ctx.rect(left, top, width, height);
    ctx.clip();

    const rugTop = top + height - RUG_HEIGHT * devicePixelRatio;
    for (const cluster of clusters) {
      const x = left + cluster.x;
      const representative = cluster.observations[0].features[0];
      const isSelected = cluster.observations.some((observation) =>
        observation.features.some((f) => f.feature_id === selectedRef.current),
      );
      const isHovered =
        hoveredObservationIdRef.current != null &&
        cluster.observations.some(
          (observation) => observation.id === hoveredObservationIdRef.current,
        );

      const color = getFeatureColor(representative.feature_id);

      // (1) 분석된 구간을 가리키는 **세로 점선** — 종전 recharts
      //     ReferenceLine(strokeDasharray="3 3")이 하던 표시다. 운영자
      //     피드백으로 복원("예전에 분석이 된 구간의 점선"). 다만 관찰이
      //     170건이면 선 170개가 크로마토그램을 덮으므로, 화면에서 겹치는
      //     것은 하나로 묶고(clusterObservationsForRug) 옅게 그린다.
      ctx.save();
      ctx.strokeStyle = color;
      // 호버 = "지금 누르면 이게 잡힌다" 예고. 선택(실선·진함)과 구분되게
      // 점선을 유지하면서 진하기만 올린다.
      ctx.globalAlpha = isSelected ? 0.9 : isHovered ? 0.7 : 0.28;
      ctx.lineWidth = (isSelected || isHovered ? 2 : 1) * devicePixelRatio;
      ctx.setLineDash(
        isSelected ? [] : [3 * devicePixelRatio, 3 * devicePixelRatio],
      );
      ctx.beginPath();
      ctx.moveTo(x, top);
      ctx.lineTo(x, top + height);
      ctx.stroke();
      ctx.restore();

      // (2) 하단 rug 눈금 — 점선보다 진하게 그려 위치를 또렷이 한다
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = (isSelected || isHovered ? 2 : 1) * devicePixelRatio;
      ctx.beginPath();
      ctx.moveTo(x, rugTop);
      ctx.lineTo(x, top + height);
      ctx.stroke();
      ctx.restore();

      // 묶인 관찰 수 — 겹쳐서 하나로 보이는 것을 숨기지 않는다
      if (cluster.size > 1) {
        ctx.save();
        ctx.fillStyle = "#475569";
        ctx.font = `${10 * devicePixelRatio}px sans-serif`;
        ctx.textAlign = "center";
        ctx.fillText(String(cluster.size), x, rugTop - 2 * devicePixelRatio);
        ctx.restore();
      }
    }
    ctx.restore();
  }, []);

  // uPlot 인스턴스는 **한 번만** 만든다.
  //
  // 종전에는 effect 의존성이 `ticData` 배열이라, 부모가 같은 내용의 새 배열을
  // 넘길 때마다 차트를 destroy/재생성했다. 실브라우저 검증에서 **드래그 중
  // 인스턴스가 사라져 확대가 전혀 동작하지 않는 것**으로 드러났다(jsdom 은
  // 캔버스를 그리지 않아 이 결함을 볼 수 없었다). 내용 서명이 같으면 다시
  // 만들지 않고, 내용이 바뀌면 setData 로 갱신한다.
  const dataSignature = `${ticData.length}:${ticData[0]?.rt ?? ""}:${
    ticData[ticData.length - 1]?.rt ?? ""
  }`;
  const ticDataRef = useRef(ticData);
  ticDataRef.current = ticData;

  // dataSignature 는 본문에서 읽지 않고 **재생성 트리거**로만 쓴다(데이터
  // 내용이 바뀔 때만 차트를 다시 만든다). 빼면 데이터가 바뀌어도 옛 차트가 남는다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: 위 사유
  useEffect(() => {
    const container = containerRef.current;
    const source = ticDataRef.current;
    if (!container || source.length === 0) return;

    const xs = source.map((point) => point.rt);
    const ys = source.map((point) =>
      Number.isFinite(point.intensity) ? point.intensity : null,
    );
    const rtMin = xs[0];
    const rtMax = xs[xs.length - 1];

    const plot = new uPlot(
      {
        width: container.clientWidth || 800,
        height: CHART_HEIGHT,
        // 시계열이 아니라 RT(분) 축
        scales: { x: { time: false } },
        // 내장 범례를 끈다 — 값은 차트 **위 커서 자리**에 띄운다.
        //
        // 2026-09-03 (운영자): "RT·Intensity 숫자가 마우스를 올렸을 때만
        // 표시되는데, 그러면 그 슬롯은 없애고 차트 위에 표시하는 게 더
        // 직관적이지 않나."
        //
        // 맞다. 커서는 차트 위에 있는데 값은 차트 아래 줄에 떠서 시선이
        // 왕복해야 했고, 올리지 않았을 때는 "RT: -- Intensity: --" 가 자리만
        // 차지했다. 위 MZRT 는 이미 값을 커서 옆 칩으로 띄우고 있어 두 플롯의
        // 표기도 어긋나 있었다. 값은 눈이 이미 가 있는 곳에 있어야 한다.
        legend: { show: false },
        axes: [
          {
            label: "RT (min)",
            stroke: "#64748b",
            grid: { stroke: "#f1f5f9" },
            // 축 이름이 눈금에서 너무 멀리 떨어져 있었다 — 붙여 준다.
            size: 44,
            labelSize: 18,
            labelGap: 0,
          },
          {
            label: "Intensity (counts)",
            stroke: "#64748b",
            grid: { stroke: "#f1f5f9" },
            // 2026-09-04 (운영자): "Intensity 는 값이 글자를 덮어서 안 보여."
            //
            // 실측: 눈금 자리는 50px 인데 `1,500,000` 은 그보다 넓어 왼쪽 축
            // 이름 위로 넘쳤다. 눈금 폭을 늘리면 가뜩이나 좁은 플롯(303px)이
            // 더 좁아지므로 **눈금 쪽을 줄인다**(1.5M · 250k).
            //
            // 줄이는 것은 눈금뿐이다 — 커서가 읽는 값은 차트 위 칩에 원래
            // 숫자 전체로 뜬다. 증거 화면에서 읽는 값을 반올림하면 안 된다.
            values: (_u, splits) => splits.map(compactIntensity),
            size: 46,
            labelSize: 18,
            labelGap: 0,
          },
        ],
        series: [
          // 축 이름은 장비·문헌 표기를 따른다(운영자 지정: Intensity 유지).
          // uPlot 범례는 커서 위치의 실제 값을 DOM 에 직접 써넣는다(React
          // 리렌더 없음) — 마우스 이동마다 상태를 갱신하면 방금 잡은 성능
          // 문제가 되살아나므로 내장 범례를 그대로 쓴다.
          { label: "RT" },
          {
            label: "Intensity",
            stroke: LINE_COLOR,
            // 원자료를 직선으로 잇는다 — 없는 곡선을 만들지 않는다.
            width: 1,
            points: { show: false },
            // null 은 선을 끊는다(결측을 이어 그리지 않는다).
            spanGaps: false,
          },
        ],
        cursor: {
          drag: { x: true, y: false, setScale: true },
          // 가로선을 끈다 — 마우스 높이를 따라갈 뿐 **아무것도 가리키지
          // 않는 선**이었다(실측: 선은 Y축 약 125만 위치인데 바로 아래
          // 숫자는 7,160 — 높이 203px 화면에서 172px 어긋남).
          // 운영자 결정 1-(c).
          y: false,
          // 곡선 위 커서 표식 — 종전에도 있었으나 선과 같은 보라색 5px 라
          // 보이지 않았다. 이제 이것이 "지금 읽고 있는 스캔"을 가리킨다.
          points: {
            size: 9,
            width: 2,
            stroke: () => "#1e293b",
            fill: () => "#ffffff",
          },
          // 커서가 놓인 **화면 1픽셀 폭 안의 실측 스캔 중 최댓값**을 가리킨다
          // (운영자 결정 2-(b)). 종전 최근접 1점 방식은 봉우리에서 18배 낮은
          // 값을 보여줬다 — 화면 1픽셀에 원자료가 13점 겹치기 때문이다.
          // 상세와 근거는 utils/ticCursor.ts.
          dataIdx: (u, seriesIdx, closestIdx) => {
            if (seriesIdx !== 1) return closestIdx;
            const left = u.cursor.left;
            if (left == null || left < 0) return closestIdx;
            return maxIndexInPixelColumn(
              u.data[0] as number[],
              u.data[1] as (number | null)[],
              u.posToVal(left - 0.5, "x"),
              u.posToVal(left + 0.5, "x"),
              closestIdx,
            );
          },
        },
        hooks: {
          draw: [drawRug],
          // 커서 값 칩 — **DOM 직접 갱신**이다. 마우스 이동마다 setState 하면
          // 원자료 수천 점을 매번 다시 그리게 된다(이 화면의 설계 원칙).
          setCursor: [
            (u) => {
              const chip = cursorChipRef.current;
              const wrapper = chartWrapperRef.current;
              if (!chip || !wrapper) return;

              // **시리즈별** 인덱스를 읽는다. `u.cursor.idx` 는 우리가 고른
              // 것이 아니라 uPlot 이 계산한 **원래의 최근접** 인덱스다 —
              // 그걸 쓰면 위 `dataIdx`(픽셀 열 최댓값)가 무시된다. 실측에서
              // 봉우리 자리에 커서를 둬도 158,697(최근접)이 나왔고, 진짜
              // 최댓값 1,314,973 은 표시되지 않았다.
              const index = u.cursor.idxs?.[1] ?? u.cursor.idx;
              const value = index == null ? null : (u.data[1][index] ?? null);
              if (index == null || value == null) {
                chip.style.display = "none";
                return;
              }

              const rt = u.data[0][index] as number;

              // **두 번째 읽기 — 가까운 봉우리 꼭대기** (2026-09-06).
              //
              // 커서 숫자는 위 규칙(픽셀 열 최댓값) 그대로다. 그 규칙은 보고
              // 지점이 **언제나 커서 자기 픽셀 열 안**이라는 보장을 준다.
              // 봉우리 찾기는 최대 10픽셀(이 화면에서 약 3.3분) 떨어진 스캔을
              // 돌려줄 수 있어, 그 값을 커서 숫자로 쓰면 그 보장이 깨진다.
              // 그래서 **이름을 붙여 따로** 보여준다 — 두 숫자가 각각 무엇인지
              // 화면이 말한다.
              //
              // 탐색은 보이는 x 범위로 자른다. 화면에 그려지지도 않은 스캔을
              // 보고하지 않기 위해서다.
              const xsAll = u.data[0] as number[];
              const ysAll = u.data[1] as (number | null)[];
              const perPx = u.posToVal(1, "x") - u.posToVal(0, "x");
              const peakIdx = peakIndexNear(
                xsAll,
                ysAll,
                index,
                perPx,
                undefined,
                undefined,
                {
                  xMin: u.scales.x.min ?? xsAll[0],
                  xMax: u.scales.x.max ?? xsAll[xsAll.length - 1],
                },
              );
              const peakValue = ysAll[peakIdx] ?? null;
              const peakLine =
                peakIdx !== index && peakValue != null
                  ? `\n가까운 봉우리 RT ${(xsAll[peakIdx] as number).toFixed(3)} · ${Math.round(peakValue).toLocaleString()}`
                  : "";

              // 두 줄로 쓴다 — TIC 패널의 플롯 폭이 300px 남짓이라 한 줄로 쓰면
              // 칩이 플롯 폭의 대부분을 덮고 왼쪽 y축 라벨과 겹친다(화면 확인).
              chip.textContent = `RT ${rt.toFixed(3)}\nIntensity ${Math.round(value).toLocaleString()}${peakLine}`;

              const overRect = u.over.getBoundingClientRect();
              const wrapperRect = wrapper.getBoundingClientRect();
              const left = u.valToPos(rt, "x", false);
              chip.style.display = "block";
              chip.style.top = `${overRect.top - wrapperRect.top + 6}px`;
              // 칩은 가운데 정렬(-translate-x-1/2)이므로 **실제 폭의 절반**만큼
              // 여유를 두고, **플롯 영역 안**으로 가둔다. 감싸개 기준으로 가두면
              // 왼쪽 끝에서 y축 라벨을 덮는다(화면 확인에서 실제로 그랬다).
              const overLeft = overRect.left - wrapperRect.left;
              const half = chip.offsetWidth / 2 + 2;
              const lo = overLeft + half;
              const hi = overLeft + overRect.width - half;
              chip.style.left = `${
                lo > hi
                  ? overLeft + overRect.width / 2 // 칩이 플롯보다 넓으면 가운데
                  : Math.min(Math.max(overLeft + left, lo), hi)
              }px`;
            },
          ],
          setScale: [
            (u, key) => {
              if (key !== "x") return;
              const { min, max } = u.scales.x;
              if (min == null || max == null) return;
              // 전 구간이면 필터 해제로 본다
              const isFull = min <= rtMin + 1e-9 && max >= rtMax - 1e-9;
              setIsZoomed(!isFull);
              setScansPerPixel(scansPerPixelIn(xs, min, max, u));
              syncNavSelection(min, max, isFull);
              handlersRef.current.onBrushChange(
                isFull ? null : { rtMin: min, rtMax: max },
              );
            },
          ],
          ready: [
            (u) => {
              // 호버 미리보기 — "지금 누르면 어느 관찰이 잡히는가"를 미리
              // 보여준다. 스냅이 조용히 일어나면 사용자는 자기가 무엇을
              // 골랐는지 누른 뒤에야 안다(운영자 요청 2026-09-03).
              // **잡을 대상이 없으면 아무것도 바꾸지 않는다** — 선택 강조는
              // 그대로 유지된다.
              u.over.addEventListener("mousemove", (event) => {
                const rect = u.over.getBoundingClientRect();
                const hoverX = event.clientX - rect.left;
                const nearest = nearestRugObservation(
                  observationsRef.current,
                  (rt) => u.valToPos(rt, "x", false),
                  hoverX,
                  CLICK_TOLERANCE_PX,
                );
                const nextId = nearest?.id ?? null;
                u.over.style.cursor = nextId ? "pointer" : "crosshair";
                if (hoveredObservationIdRef.current === nextId) return;
                hoveredObservationIdRef.current = nextId;
                u.redraw();
              });

              u.over.addEventListener("mouseleave", () => {
                if (hoveredObservationIdRef.current === null) return;
                hoveredObservationIdRef.current = null;
                u.redraw();
              });

              u.over.addEventListener("click", (event) => {
                const rect = u.over.getBoundingClientRect();
                const clickX = event.clientX - rect.left;
                const toPixel = (rt: number) => u.valToPos(rt, "x", false);
                const nearest = nearestRugObservation(
                  observationsRef.current,
                  toPixel,
                  clickX,
                  CLICK_TOLERANCE_PX,
                );
                if (nearest) {
                  const here = nearest.features.find(
                    (f) => f.feature_id === selectedRef.current,
                  );
                  handlersRef.current.onFeatureSelect(
                    (here ?? nearest.features[0]).feature_id,
                  );
                  return;
                }
                handlersRef.current.onChartClick?.(u.posToVal(clickX, "x"));
              });
            },
          ],
        },
      },
      [xs, ys] as uPlot.AlignedData,
      container,
    );
    /** 본 차트의 확대 구간을 하단 개요 막대에 반영한다(양방향 동기화). */
    function syncNavSelection(min: number, max: number, isFull: boolean) {
      const nav = navPlotRef.current;
      if (!nav) return;
      if (isFull) {
        nav.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false);
        return;
      }
      const left = nav.valToPos(min, "x", false);
      const right = nav.valToPos(max, "x", false);
      nav.setSelect(
        {
          left,
          top: 0,
          width: Math.max(right - left, 2),
          height: nav.bbox.height / devicePixelRatio,
        },
        false,
      );
    }

    // 초기 x 범위를 **명시적으로 확정**한다.
    //
    // uPlot 은 생성 시 자동 범위를 마이크로태스크 커밋으로 미루는데, React
    // StrictMode 의 이중 마운트(생성→파기→재생성)와 겹치면 그 커밋이 적용되지
    // 않은 채 남았다. 실브라우저 검증에서 `scales.x.min/max` 가 null 로
    // 남아 **드래그 확대가 전혀 동작하지 않는 것**으로 드러났다(jsdom 은
    // 캔버스를 그리지 않아 볼 수 없는 결함이다). batch 는 커밋을 동기로
    // 수행하므로 이 시점에 범위가 확정된다.
    plot.batch(() => {
      plot.setScale("x", { min: rtMin, max: rtMax });
    });

    plotRef.current = plot;
    fullRangeRef.current = { min: rtMin, max: rtMax };

    // ── 하단 개요 막대(네비게이터) ────────────────────────────────────────
    // 종전 recharts Brush 자리를 대신한다. 전 구간을 늘 보여 주고, 현재 확대
    // 구간을 밝은 영역으로 표시하며, 여기서 끌어 구간을 고를 수 있다.
    // "확대할 수 있다 / 지금 확대 중이다 / 어디를 보고 있다"가 화면에 남는다.
    const navContainer = navContainerRef.current;
    if (navContainer) {
      const nav = new uPlot(
        {
          width: navContainer.clientWidth || 800,
          height: NAV_HEIGHT,
          scales: { x: { time: false } },
          axes: [{ show: false }, { show: false }],
          legend: { show: false },
          series: [
            {},
            {
              stroke: "#c4b5fd",
              fill: "rgba(196,181,253,0.35)",
              width: 1,
              points: { show: false },
              spanGaps: false,
            },
          ],
          cursor: {
            // 여기서는 확대가 아니라 **구간 선택**이다(본 차트를 움직인다).
            drag: { x: true, y: false, setScale: false },
            // 개요 막대는 값을 읽는 면이 아니다 — 십자 점선을 끈다.
            x: false,
            y: false,
            points: { show: false },
          },
          hooks: {
            setSelect: [
              (u) => {
                if (u.select.width <= 2) return;
                const from = u.posToVal(u.select.left, "x");
                const to = u.posToVal(u.select.left + u.select.width, "x");
                plotRef.current?.setScale("x", { min: from, max: to });
              },
            ],
          },
        },
        [xs, ys] as uPlot.AlignedData,
        navContainer,
      );
      nav.batch(() => {
        nav.setScale("x", { min: rtMin, max: rtMax });
      });
      navPlotRef.current = nav;

      // ── 음영(현재 보고 있는 구간) 잡아 이동·폭 조절 ────────────────────
      // 운영자 승인(2026-09-03). 새 구간을 매번 다시 그리는 것만 되면 "조금만
      // 옆으로" 가 안 된다. 종전 recharts Brush 는 창을 끌어 옮기고 양 끝을
      // 잡아 늘릴 수 있었다 — 같은 조작을 되살린다.
      const EDGE_PX = 6;
      type DragMode = "pan" | "resize-left" | "resize-right";
      let mode: DragMode | null = null;
      let grabOffsetPx = 0;
      let frame: number | null = null;
      let pendingRange: { min: number; max: number } | null = null;

      /** 현재 음영의 화면 좌표(px). 확대 중이 아니면 null. */
      function currentWindowPx(): { left: number; right: number } | null {
        const main = plotRef.current;
        if (!main) return null;
        const { min, max } = main.scales.x;
        if (min == null || max == null) return null;
        if (min <= rtMin + 1e-9 && max >= rtMax - 1e-9) return null;
        return {
          left: nav.valToPos(min, "x", false),
          right: nav.valToPos(max, "x", false),
        };
      }

      /** 본 차트 갱신은 프레임당 1회 — 마우스 이동마다 수천 점을 다시 그리면
          방금 잡은 성능 문제가 되살아난다. */
      function applyRangeThrottled(min: number, max: number) {
        pendingRange = { min, max };
        if (frame !== null) return;
        frame = requestAnimationFrame(() => {
          frame = null;
          const range = pendingRange;
          if (!range) return;
          plotRef.current?.setScale("x", range);
        });
      }

      function modeAt(x: number): DragMode | null {
        const win = currentWindowPx();
        if (!win) return null;
        if (Math.abs(x - win.left) <= EDGE_PX) return "resize-left";
        if (Math.abs(x - win.right) <= EDGE_PX) return "resize-right";
        if (x > win.left && x < win.right) return "pan";
        return null;
      }

      function localX(event: MouseEvent): number {
        return event.clientX - nav.over.getBoundingClientRect().left;
      }

      // 잡을 수 있음을 커서로 알린다(잡을 데가 없으면 종전대로 새 선택).
      nav.over.addEventListener("mousemove", (event) => {
        if (mode) return;
        const at = modeAt(localX(event));
        nav.over.style.cursor =
          at === "pan" ? "grab" : at ? "ew-resize" : "crosshair";
      });

      nav.over.addEventListener(
        "mousedown",
        (event) => {
          const x = localX(event);
          const at = modeAt(x);
          if (!at) return; // 음영 밖 → uPlot 의 새 구간 선택에 맡긴다
          const win = currentWindowPx();
          if (!win) return;
          // uPlot 의 드래그 선택이 함께 시작되지 않게 가로챈다
          event.stopPropagation();
          event.preventDefault();
          mode = at;
          grabOffsetPx = x - win.left;
          nav.over.style.cursor = at === "pan" ? "grabbing" : "ew-resize";
        },
        { capture: true },
      );

      const onWindowMove = (event: MouseEvent) => {
        if (!mode) return;
        const win = currentWindowPx();
        if (!win) return;
        const x = localX(event);
        const widthPx = win.right - win.left;

        if (mode === "pan") {
          // 폭은 유지하고 창만 민다. 양 끝에서는 더 밀리지 않는다.
          const plotWidthPx = nav.bbox.width / devicePixelRatio;
          const left = Math.min(
            Math.max(x - grabOffsetPx, 0),
            Math.max(plotWidthPx - widthPx, 0),
          );
          applyRangeThrottled(
            nav.posToVal(left, "x"),
            nav.posToVal(left + widthPx, "x"),
          );
          return;
        }

        // 폭 조절 — 반대쪽 끝은 고정, 최소 폭 보장
        const anchorPx = mode === "resize-left" ? win.right : win.left;
        const movedPx =
          mode === "resize-left"
            ? Math.min(x, anchorPx - EDGE_PX)
            : Math.max(x, anchorPx + EDGE_PX);
        const a = nav.posToVal(Math.min(anchorPx, movedPx), "x");
        const b = nav.posToVal(Math.max(anchorPx, movedPx), "x");
        applyRangeThrottled(Math.max(a, rtMin), Math.min(b, rtMax));
      };

      const onWindowUp = () => {
        if (!mode) return;
        mode = null;
        nav.over.style.cursor = "crosshair";
      };

      window.addEventListener("mousemove", onWindowMove);
      window.addEventListener("mouseup", onWindowUp);
      navCleanupRef.current = () => {
        window.removeEventListener("mousemove", onWindowMove);
        window.removeEventListener("mouseup", onWindowUp);
        if (frame !== null) cancelAnimationFrame(frame);
      };
    }

    const resize = new ResizeObserver(() => {
      plot.setSize({
        width: container.clientWidth || 800,
        height: CHART_HEIGHT,
      });
      const nav = navPlotRef.current;
      if (nav && navContainerRef.current) {
        nav.setSize({
          width: navContainerRef.current.clientWidth || 800,
          height: NAV_HEIGHT,
        });
      }
    });
    resize.observe(container);

    return () => {
      resize.disconnect();
      plot.destroy();
      plotRef.current = null;
      navCleanupRef.current?.();
      navCleanupRef.current = null;
      navPlotRef.current?.destroy();
      navPlotRef.current = null;
    };
    // dataSignature 가 같으면 재생성하지 않는다(드래그 중 파괴 방지).
  }, [dataSignature, drawRug]);

  /** 전체 구간으로 되돌린다 — 더블클릭과 같은 동작을 **보이는 버튼**으로. */
  const resetZoom = useCallback(() => {
    const full = fullRangeRef.current;
    if (!full) return;
    plotRef.current?.setScale("x", { min: full.min, max: full.max });
  }, []);

  // 선택 변경은 인스턴스를 다시 만들지 않고 **다시 그리기만** 한다.
  // 선택·후보가 바뀌면 rug 를 다시 그려야 한다. 본문은 redraw() 만 부르므로
  // 린트는 불필요하다고 보지만, 이 둘이 **다시 그릴 이유**다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: 위 사유
  useEffect(() => {
    plotRef.current?.redraw();
  }, [selectedFeature, selectedScanRT, features]);

  // 선택한 관찰이 **확대 구간 밖이면 그쪽으로 따라간다**.
  //
  // 2026-09-03 (운영자 지적): 표에서 고르면 MS·상세 패널은 그 스캔으로 가는데
  // 두 플롯은 확대 구간에 갇혀 선택을 보여주지 못했다(실측: TIC 를 RT 3.3~8.0
  // 으로 확대한 채 RT 10.38 관찰을 고르면 TIC 에도 없고, 같은 구간으로
  // 걸러지는 MZRT 에도 점이 없다).
  //
  // 확대 배율(폭)은 **유지**하고 선택 지점을 가운데로 옮긴다 — 사용자가 정한
  // 상세 수준을 빼앗지 않으면서 고른 것을 보여준다. MZRT 는 같은 RT 구간으로
  // 걸러지므로 함께 따라온다.
  useEffect(() => {
    const plot = plotRef.current;
    const full = fullRangeRef.current;
    if (!plot || !full || !selectedFeature) return;

    const target = observationsRef.current.find((observation) =>
      observation.features.some((f) => f.feature_id === selectedFeature),
    );
    if (!target) return;

    const { min, max } = plot.scales.x;
    if (min == null || max == null) return;
    if (target.rt >= min && target.rt <= max) return; // 이미 보인다

    const width = max - min;
    let nextMin = target.rt - width / 2;
    let nextMax = target.rt + width / 2;
    if (nextMin < full.min) {
      nextMin = full.min;
      nextMax = Math.min(full.min + width, full.max);
    }
    if (nextMax > full.max) {
      nextMax = full.max;
      nextMin = Math.max(full.max - width, full.min);
    }
    plot.setScale("x", { min: nextMin, max: nextMax });
  }, [selectedFeature]);

  const observationCount = observationsRef.current.length;

  return (
    <div className={panelCls("standard", "h-full")}>
      <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center flex-wrap">
        <span className="w-2 h-2 bg-purple-500 rounded-full mr-2" />
        <span>TIC Plot</span>
        <span className="ml-2 text-sm font-normal text-gray-500">
          (총이온크로마토그램)
        </span>
      </h3>
      {/* 무엇을 그리고 있는지 밝힌다 — 축약본이 아니라 원자료 전량이다. */}
      <div className="mb-3 flex items-start justify-between gap-3">
        {/* 무엇을 보고 있는지와 커서 값의 뜻을 **한자리에** 모은다.
            2026-09-04 (운영자): 커서 값 설명이 개요 막대 바로 위에 있어
            거슬린다는 지적 — 차트와 개요 막대 사이를 비우고 위로 올렸다.
            표시 규칙은 화면 어딘가에 반드시 쓰여 있어야 한다(증거 화면에서
            숫자의 뜻을 사람이 추측하게 두면 안 된다). */}
        {/* 2026-09-06: 문단 둘을 한 줄로 합쳤다(운영자: "쓸데없는 말이 많아.
            핵심만 간결하게"). 상시 표시는 **무엇을 그렸는가** 뿐이고, 커서
            값의 계산 규칙·'가까운 봉우리'의 범위·개요 막대 조작은 ⓘ 로
            옮겼다 — 지운 것이 아니라 자리를 바꾼 것이다. 증거 화면에서
            숫자의 뜻을 추측하게 두면 안 된다는 원칙은 그대로다. */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-500">
          <span>
            원자료 {ticData.length.toLocaleString()}점 전량 · AI 후보가 기록된
            관찰 {observationCount}건
          </span>
          <InfoDot
            note={[
              `커서 값 — ${ticCursorNote(scansPerPixel)}`,
              "아래 눈금 — AI 후보가 기록된 관찰(스캔)이 놓인 자리입니다.",
              "확대 — 그래프나 개요 막대를 드래그하면 그 RT 구간만 보고, 아래 플롯도 그 구간으로 걸립니다.",
            ].join("\n")}
          />
        </div>
        {/* 확대 중일 때만 나타난다 — 종전 Brush 막대가 하던 "되돌리기" 역할.
            더블클릭으로도 되지만 화면에 쓰여 있지 않으면 없는 기능이다. */}
        {isZoomed && (
          <button
            type="button"
            onClick={resetZoom}
            className="shrink-0 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
          >
            전체 구간 보기
          </button>
        )}
      </div>
      {ticData.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center">
          <p className="text-sm font-medium text-gray-700">
            TIC 데이터 없음 — 크로마토그램을 표시할 수 없음
          </p>
          <p className="mt-2 text-xs text-gray-500">
            원본 장비 데이터에 TIC 미수집 (결측이며 비검출 아님)
          </p>
        </div>
      ) : (
        <>
          <div ref={chartWrapperRef} className="relative">
            {/* 커서 값 — 차트 위, 커서가 가리키는 스캔 자리에 뜬다.
                값의 뜻(1픽셀 안 최댓값)은 아래 문구가 밝힌다. */}
            <div
              ref={cursorChipRef}
              data-testid="tic-cursor-chip"
              aria-hidden="true"
              className="pointer-events-none absolute z-10 -translate-x-1/2 whitespace-pre-line rounded border border-slate-200 bg-white/95 px-1.5 py-0.5 text-center text-[11px] font-medium leading-tight text-slate-700 shadow-sm"
              style={{ display: "none" }}
            />
            <div
              ref={containerRef}
              data-testid="tic-uplot-container"
              role="img"
              aria-label={`총이온크로마토그램 — 원자료 ${ticData.length}점, 관찰 ${observationCount}건. 관찰 선택은 아래 관찰 표에서도 할 수 있습니다.`}
              className="w-full tic-uplot"
            />
          </div>
          {/* 개요 막대 — 전 구간을 늘 보여 주고 현재 확대 구간을 표시한다.
              recharts Brush 가 하던 역할(맥락 유지·구간 이동)을 대신한다. */}
          <div
            ref={navContainerRef}
            data-testid="tic-uplot-nav"
            role="img"
            aria-label="크로마토그램 개요 막대 — 끌어서 볼 구간을 고릅니다"
            className="w-full"
          />
          <p className="mt-1 text-[11px] text-gray-400">
            {ticNavCaption(isZoomed)}
          </p>
        </>
      )}
    </div>
  );
};

export default memo(TICPlotUplot);
